// Custom hook for calculating shipping costs
import { useState } from 'react';
import { getAdminModeHeaders } from '../utils/adminMode';

interface ShippingRequest {
  frameTypeId: string;
  frameSizeId: string;
  recipient: {
    firstName: string;
    lastName: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    phone?: string;
    company?: string;
  };
}

interface ShippingOption {
  carrier: string;
  method: string;
  cost: number;
  available: boolean;
  lumaMethod: string;
  note?: string;
}

interface ShippingResponse {
  success: boolean;
  error?: string;
  frameType: string;
  frameSize: string;
  shippingOptions: {
    ground: ShippingOption;
    overnight: ShippingOption;
  };
  timestamp: string;
}

export const useShippingCalculator = () => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [shippingCosts, setShippingCosts] = useState<ShippingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateShipping = async (request: ShippingRequest): Promise<ShippingResponse | null> => {
    setIsCalculating(true);
    setError(null);

    try {
      console.log('Calculating shipping costs for:', request);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-shipping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          ...getAdminModeHeaders(),
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`Failed to calculate shipping: ${response.status} ${response.statusText}`);
      }

      const shippingData: ShippingResponse = await response.json();
      console.log('Shipping calculation response:', shippingData);

      setShippingCosts(shippingData);
      return shippingData;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to calculate shipping costs';
      console.error('Shipping calculation error:', errorMessage);
      setError(errorMessage);
      return null;
    } finally {
      setIsCalculating(false);
    }
  };

  const clearShippingData = () => {
    setShippingCosts(null);
    setError(null);
  };

  return {
    calculateShipping,
    clearShippingData,
    isCalculating,
    shippingCosts,
    error
  };
};